/**
 * Gamification Theme Context
 *
 * Follows the same TanStack Query pattern as useThemePreferences.ts.
 * - Single composite query per user (pref → theme → default → fallback)
 * - No sequential Supabase waterfalls; user comes from AuthContext, not getUser()
 * - Hardcoded FALLBACK_THEME is returned on query error — UI is never unprotected
 * - asThemeOverrides strips null values so config JSON cannot silently clobber
 *   required fields (currency, ranks, terminology) with null
 * - getProgressToNextRank guards against division by zero from misconfigured ranks
 * - switchTheme applies an optimistic update before the RPC round-trip
 */

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { GamificationTheme, RankDefinition, ThemedGamificationStats } from '@flowstate/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

// ─── Fallback theme ──────────────────────────────────────────────────────────
// Returned when the DB is unreachable or returns a row missing required fields.
// All required fields are populated so consumers never need to null-check.

const FALLBACK_THEME: GamificationTheme = {
  id: 'fallback',
  name: 'xp',
  displayName: 'XP System',
  description: 'Default XP system',
  category: 'default',
  version: '1.0.0',
  currency: {
    singular: 'XP',
    plural: 'XP',
    icon: '⚡',
    color: '#FFD700',
    abbreviation: 'XP',
  },
  ranks: [],
  styling: {
    primaryColor: '#3b82f6',
    secondaryColor: '#8b5cf6',
    accentColor: '#f59e0b',
  },
  badges: {
    shape: 'circle',
    borderStyle: 'glow',
    rarityColors: {
      common: '#94a3b8',
      rare: '#3b82f6',
      epic: '#8b5cf6',
      legendary: '#f59e0b',
    },
  },
  terminology: {
    points: 'XP',
    leaderboard: 'Leaderboard',
    streak: 'Streak',
    level: 'Rank',
    achievement: 'Achievement',
    reward: 'Reward',
  },
  isActive: true,
  isDefault: true,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Merges a DB row's `config` JSON field onto the base row.
 * Strips null/undefined values so config JSON cannot silently clobber required
 * fields like `currency` with null.
 */
function asThemeOverrides(config: unknown): Partial<GamificationTheme> {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return {};
  return Object.fromEntries(
    Object.entries(config as Record<string, unknown>).filter(([, v]) => v != null),
  ) as Partial<GamificationTheme>;
}

/**
 * Merges a raw DB row + its `config` field and enforces required field presence.
 */
function validateAndMergeTheme(raw: Record<string, unknown>): GamificationTheme {
  const overrides = asThemeOverrides(raw.config);
  const merged: Record<string, unknown> = { ...raw, ...overrides };

  if (!merged.currency || typeof merged.currency !== 'object') {
    logger.debug('[GamificationTheme] DB row missing currency field; using fallback currency.', {
      themeId: raw.id,
    });
    merged.currency = FALLBACK_THEME.currency;
  }
  if (!Array.isArray(merged.ranks)) {
    merged.ranks = [];
  }
  if (!merged.terminology || typeof merged.terminology !== 'object') {
    merged.terminology = FALLBACK_THEME.terminology;
  }

  // Ensure all required GamificationTheme fields are present
  return {
    id: (merged.id as string) ?? FALLBACK_THEME.id,
    name: (merged.name as string) ?? FALLBACK_THEME.name,
    displayName: (merged.displayName as string) ?? FALLBACK_THEME.displayName,
    description: (merged.description as string) ?? FALLBACK_THEME.description,
    category: (merged.category as GamificationTheme['category']) ?? FALLBACK_THEME.category,
    version: (merged.version as string) ?? FALLBACK_THEME.version,
    currency: merged.currency as GamificationTheme['currency'],
    ranks: merged.ranks as GamificationTheme['ranks'],
    styling: (merged.styling as GamificationTheme['styling']) ?? FALLBACK_THEME.styling,
    badges: (merged.badges as GamificationTheme['badges']) ?? FALLBACK_THEME.badges,
    terminology: merged.terminology as GamificationTheme['terminology'],
    isActive: (merged.isActive as boolean) ?? FALLBACK_THEME.isActive,
    isDefault: (merged.isDefault as boolean) ?? FALLBACK_THEME.isDefault,
  };
}

// ─── Query keys ───────────────────────────────────────────────────────────────

export const gamificationThemeKeys = {
  all: ['gamification-theme'] as const,
  byUser: (userId: string | undefined) =>
    [...gamificationThemeKeys.all, userId ?? 'anonymous'] as const,
};

// ─── Fetch ───────────────────────────────────────────────────────────────────

async function fetchGamificationTheme(userId: string | undefined): Promise<GamificationTheme> {
  if (!userId) {
    return FALLBACK_THEME;
  }

  const { data: pref, error: prefError } = await supabase
      .from('user_theme_preferences')
      .select('theme_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (prefError) {
      logger.warn('[GamificationTheme] Could not fetch user preference; falling back.', {
        error: prefError,
      });
    }

    if (pref?.theme_id) {
      const { data: userTheme, error: themeError } = await supabase
        .from('gamification_themes')
        .select('*')
        .eq('id', pref.theme_id)
        .eq('is_active', true)
        .maybeSingle();

      if (!themeError && userTheme) {
        return validateAndMergeTheme(userTheme as Record<string, unknown>);
      }
      if (themeError) {
        logger.warn('[GamificationTheme] User theme fetch failed; falling back to default.', {
          themeId: pref.theme_id,
          error: themeError,
        });
      }
    }

  const { data: defaultTheme, error: defaultError } = await supabase
    .from('gamification_themes')
    .select('*')
    .eq('is_default', true)
    .eq('is_active', true)
    .maybeSingle();

  if (!defaultError && defaultTheme) {
    return validateAndMergeTheme(defaultTheme as Record<string, unknown>);
  }
  if (defaultError) {
    logger.error('[GamificationTheme] Default theme fetch failed; using hardcoded fallback.', {
      error: defaultError,
    });
  }

  return FALLBACK_THEME;
}

interface GamificationThemeContextValue {
  theme: GamificationTheme | null;
  isLoading: boolean;
  error: Error | null;
  
  // Theme operations
  switchTheme: (themeName: string) => Promise<void>;
  refreshTheme: () => Promise<void>;
  
  // Helper functions
  getCurrencyName: (count?: number) => string;
  getRankForPoints: (points: number) => RankDefinition | null;
  getNextRank: (points: number) => RankDefinition | null;
  getProgressToNextRank: (points: number) => number;
  formatPoints: (points: number) => string;
  formatRank: (rank: RankDefinition) => string;
  getThemedStats: (stats: {
    totalPoints: number;
    pointsToday?: number;
    pointsThisWeek?: number;
    pointsThisMonth?: number;
    currentStreak?: number;
    longestStreak?: number;
    tasksCompleted?: number;
  }) => ThemedGamificationStats | null;
}

const GamificationThemeContext = createContext<GamificationThemeContextValue | undefined>(
  undefined,
);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const GamificationThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const {
    data: theme = FALLBACK_THEME,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: gamificationThemeKeys.byUser(userId),
    queryFn: () => fetchGamificationTheme(userId),
    // Gamification themes rarely change mid-session; 5 min stale time is appropriate
    staleTime: 5 * 60 * 1000,
    // Do not throw — return null and let the fallback inside fetchGamificationTheme handle it
    throwOnError: false,
    enabled: !!userId,
  });

  const switchTheme = useCallback(
    async (themeName: string) => {
      if (!userId) throw new Error('Must be authenticated to switch themes');

      // Optimistic: show the new name immediately
      queryClient.setQueryData(
        gamificationThemeKeys.byUser(userId),
        (old: GamificationTheme | undefined) => (old ? { ...old, name: themeName } : old),
      );

      try {
        const { error: rpcError } = await supabase.rpc('set_user_theme', {
          user_id_param: userId,
          theme_name_param: themeName,
        });
        if (rpcError) throw rpcError;
      } catch (err) {
        // Roll back on failure; a fresh fetch will restore the real state
        queryClient.invalidateQueries({ queryKey: gamificationThemeKeys.byUser(userId) });
        logger.error('[GamificationTheme] switchTheme RPC failed; rolled back.', { error: err });
        throw err;
      }

      // Invalidate to pull the full new theme row
      queryClient.invalidateQueries({ queryKey: gamificationThemeKeys.byUser(userId) });
    },
    [userId, queryClient],
  );

  const refreshTheme = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const getCurrencyName = useCallback(
    (count: number = 1): string => {
      if (!theme?.currency) return 'Points';
      return count === 1 ? theme.currency.singular : theme.currency.plural;
    },
    [theme],
  );

  const getRankForPoints = useCallback(
    (points: number): RankDefinition | null => {
      if (!theme?.ranks?.length) return null;
      const sorted = [...theme.ranks].sort((a, b) => b.thresholdPoints - a.thresholdPoints);
      return sorted.find((r) => points >= r.thresholdPoints) ?? theme.ranks[0];
    },
    [theme],
  );

  const getNextRank = useCallback(
    (points: number): RankDefinition | null => {
      if (!theme?.ranks) return null;
      const current = getRankForPoints(points);
      if (!current) return null;
      return (
        [...theme.ranks]
          .sort((a, b) => a.thresholdPoints - b.thresholdPoints)
          .find((r) => r.level > current.level) ?? null
      );
    },
    [theme, getRankForPoints],
  );

  const getProgressToNextRank = useCallback(
    (points: number): number => {
      const current = getRankForPoints(points);
      const next = getNextRank(points);
      if (!current || !next) return 100;
      const range = next.thresholdPoints - current.thresholdPoints;
      // Guard: misconfigured ranks with equal thresholds must not divide by zero
      if (range <= 0) return 100;
      return Math.min(100, Math.round(((points - current.thresholdPoints) / range) * 100));
    },
    [getRankForPoints, getNextRank],
  );

  const formatPoints = useCallback(
    (points: number): string => `${points.toLocaleString()} ${getCurrencyName(points)}`,
    [getCurrencyName],
  );

  const formatRank = useCallback(
    (rank: RankDefinition): string => `${rank.emoji} ${rank.name}`,
    [],
  );

  const getThemedStats = useCallback(
    (stats: {
      totalPoints: number;
      pointsToday?: number;
      pointsThisWeek?: number;
      pointsThisMonth?: number;
      currentStreak?: number;
      longestStreak?: number;
      tasksCompleted?: number;
    }): ThemedGamificationStats | null => {
      if (!theme) return null;
      const currentRank = getRankForPoints(stats.totalPoints);
      if (!currentRank) return null;
      const nextRank = getNextRank(stats.totalPoints);
      const progressToNextRank = getProgressToNextRank(stats.totalPoints);

      return {
        totalPoints: stats.totalPoints,
        pointsToday: stats.pointsToday ?? 0,
        pointsThisWeek: stats.pointsThisWeek ?? 0,
        pointsThisMonth: stats.pointsThisMonth ?? 0,
        currentStreak: stats.currentStreak ?? 0,
        longestStreak: stats.longestStreak ?? 0,
        tasksCompleted: stats.tasksCompleted ?? 0,
        currentRank,
        nextRank: nextRank ?? undefined,
        progressToNextRank,
        theme,
        formattedPoints: formatPoints(stats.totalPoints),
        formattedRank: formatRank(currentRank),
      };
    },
    [theme, getRankForPoints, getNextRank, getProgressToNextRank, formatPoints, formatRank],
  );

  const value = useMemo<GamificationThemeContextValue>(
    () => ({
      theme,
      isLoading,
      error: error as Error | null,
      switchTheme,
      refreshTheme,
      getCurrencyName,
      getRankForPoints,
      getNextRank,
      getProgressToNextRank,
      formatPoints,
      formatRank,
      getThemedStats,
    }),
    [
      theme,
      isLoading,
      error,
      switchTheme,
      refreshTheme,
      getCurrencyName,
      getRankForPoints,
      getNextRank,
      getProgressToNextRank,
      formatPoints,
      formatRank,
      getThemedStats,
    ],
  );

  return (
    <GamificationThemeContext.Provider value={value}>
      {children}
    </GamificationThemeContext.Provider>
  );
};

export const useGamificationTheme = (): GamificationThemeContextValue => {
  const context = useContext(GamificationThemeContext);
  if (context === undefined) {
    throw new Error('useGamificationTheme must be used within a GamificationThemeProvider');
  }
  return context;
};
